import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { MapPin, Clock, Star, ArrowLeft, ArrowRight, Calendar, CheckCircle, Play, Youtube, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import ShareButton from '../components/ShareButton';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

const TempleDetailPage = () => {
  const { templeId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const [temple, setTemple] = useState(null);
  const [pujas, setPujas] = useState([]);
  const [offeringPujas, setOfferingPujas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [templeRes, pujasRes] = await Promise.all([
          axios.get(`${API}/temples/${templeId}`),
          axios.get(`${API}/pujas`, { params: { temple_id: templeId } })
        ]);
        setTemple(templeRes.data);
        const allPujas = pujasRes.data;
        setPujas(allPujas.filter(p => !p.is_offering));
        setOfferingPujas(allPujas.filter(p => p.is_offering));
      } catch (error) {
        console.error('Error fetching temple:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [templeId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!temple) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Temple not found</p>
      </div>
    );
  }

  const categoryLabels = {
    other: 'Sacred Temple'
  };

  const handleOfflineInterest = async () => {
    if (!user) {
      toast.error('Please login to express interest');
      navigate('/login');
      return;
    }

    try {
      await axios.post(`${API}/offline-interest`, { temple_id: templeId }, { withCredentials: true });
      toast.success('Interest recorded! We will contact you soon.');
    } catch (error) {
      console.error('Error recording interest:', error);
      toast.error('Failed to record interest. Please try again.');
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white pt-20">
        {/* Hero */}
        <div className="relative h-[50vh] min-h-[400px]">
          <img
            src={temple.image_url}
            alt={temple.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="absolute top-24 left-6 flex items-center space-x-2 text-white/90 hover:text-white transition-colors z-10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('common.backToTemples')}</span>
          </button>

          {/* Share Button */}
          <div className="absolute top-24 right-6 z-10">
            <ShareButton
              url={window.location.href}
              title={temple.name}
              text={`Explore ${temple.name} on Paramparik Pooja!`}
              className="!w-10 !h-10 !bg-white/20 !border-white/30 !text-white hover:!bg-white/40"
            />
          </div>

          {/* Temple Info */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="max-w-7xl mx-auto">
              <span className="inline-block px-3 py-1 bg-[#FF6B00] text-white text-sm font-semibold rounded-full mb-4">
                {categoryLabels[temple.category]}
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-2">
                {temple.name}
              </h1>
              <div className="flex items-center space-x-2 text-white/90">
                <MapPin className="w-5 h-5" />
                <span>{temple.location}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* About */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-orange-100/50"
              >
                <h2 className="font-heading text-2xl font-bold text-[#2D2D2D] mb-4">{t('temples.about')}</h2>
                <p className="text-[#4A5568] leading-relaxed">
                  {lang === 'hi' && temple.description_hi ? temple.description_hi : temple.description}
                </p>
                
                {/* Authenticity Badges */}
                <div className="flex flex-wrap gap-3 mt-6">
                  <div className="authenticity-badge">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {t('temples.badges.direct')}
                  </div>
                  <div className="authenticity-badge">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {t('temples.badges.vedic')}
                  </div>
                  <div className="authenticity-badge">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {t('temples.badges.prasad')}
                  </div>
                </div>
              </motion.div>

              {/* Available Pujas */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h2 className="font-heading text-2xl font-bold text-[#1A202C] mb-6">{t('temples.availablePujas')}</h2>
                <div className="space-y-4">
                  {pujas.map((puja, index) => (
                    <motion.div
                      key={puja.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-orange-100/50 group hover:border-[#FF6B00]/30 transition-colors"
                      data-testid={`puja-card-${puja.id}`}
                    >
                      <div className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <h3 className="font-heading text-xl font-bold text-[#1A202C] group-hover:text-[#FF6B00] transition-colors">
                                {puja.name}
                              </h3>
                              <ShareButton
                                url={`${window.location.origin}/temples/${templeId}/book/${puja.id}`}
                                title={puja.name}
                                text={`Book ${puja.name} at ${temple.name} on Paramparik Pooja!`}
                              />
                            </div>
                            <p className="text-[#718096] text-sm mt-1 line-clamp-2">{puja.description}</p>
                            <div className="flex items-center space-x-4 mt-3">
                              <div className="flex items-center space-x-1 text-sm text-[#4A5568]">
                                <Clock className="w-4 h-4 text-[#FF6B00]" />
                                <span>{puja.duration}</span>
                              </div>
                            </div>
                            {/* Benefits */}
                            <div className="flex flex-wrap gap-2 mt-3">
                              {puja.benefits?.slice(0, 2).map((benefit, i) => (
                                <span key={i} className="text-xs bg-[#FFF9F0] text-[#4A5568] px-2 py-1 rounded-lg">
                                  {benefit}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex flex-col items-end space-y-3">
                            <span className="price-tag text-2xl">₹{puja.price?.toLocaleString()}</span>
                            <Link to={`/temples/${templeId}/book/${puja.id}`}>
                              <Button 
                                className="btn-saffron rounded-full px-6"
                                data-testid={`book-puja-btn-${puja.id}`}
                              >
                                {t('temples.bookNow')}
                                <ArrowRight className="ml-2 w-4 h-4" />
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>

                      {/* YouTube Video Section */}
                      {puja.youtube_url && getYouTubeEmbedUrl(puja.youtube_url) && (
                        <div className="border-t border-orange-100/50 p-4 bg-[#FFFAF3]">
                          <div className="flex items-center gap-2 mb-3">
                            <Youtube className="w-5 h-5 text-red-500" />
                            <span className="text-sm font-semibold text-[#1A202C]">{t('temples.watchPrevious')}</span>
                          </div>
                          <div className="aspect-video rounded-xl overflow-hidden">
                            <iframe
                              src={getYouTubeEmbedUrl(puja.youtube_url)}
                              title={`${puja.name} video`}
                              className="w-full h-full"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              data-testid={`puja-video-${puja.id}`}
                            />
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* ══ TEMPLE OFFERINGS ══ */}
              {offeringPujas.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mt-10"
                >
                  {/* Section Header */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center shadow-md">
                      <span className="text-lg">🪔</span>
                    </div>
                    <div>
                      <h2 className="font-heading text-2xl font-bold text-gray-900">Temple Offerings</h2>
                      <p className="text-sm text-purple-600 font-medium">Special offerings for divine grace &amp; blessings</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {offeringPujas.map((puja, index) => (
                      <motion.div
                        key={puja.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(100,0,180,0.06)] border border-purple-100/60 group hover:border-purple-300/60 transition-colors"
                        data-testid={`offering-card-${puja.id}`}
                      >
                        <div className="p-6">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs font-bold rounded-full">🪔 Offering</span>
                                <h3 className="font-heading text-xl font-bold text-gray-900 group-hover:text-purple-700 transition-colors">
                                  {puja.name}
                                </h3>
                                <ShareButton
                                  url={`${window.location.origin}/temples/${templeId}/book/${puja.id}`}
                                  title={puja.name}
                                  text={`Book ${puja.name} offering at ${temple.name}!`}
                                />
                              </div>
                              <p className="text-gray-500 text-sm mt-1 line-clamp-2">{puja.description}</p>
                              <div className="flex items-center space-x-4 mt-3">
                                <div className="flex items-center space-x-1 text-sm text-gray-600">
                                  <Clock className="w-4 h-4 text-purple-500" />
                                  <span>{puja.duration}</span>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2 mt-3">
                                {puja.benefits?.slice(0, 2).map((benefit, i) => (
                                  <span key={i} className="text-xs bg-purple-50 text-purple-600 px-2 py-1 rounded-lg">{benefit}</span>
                                ))}
                              </div>
                            </div>
                            <div className="flex flex-col items-end space-y-3">
                              <span className="text-2xl font-black text-purple-700">₹{puja.price?.toLocaleString()}</span>
                              <Link to={`/temples/${templeId}/book/${puja.id}`}>
                                <Button
                                  className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-full px-6 border-0"
                                  data-testid={`book-offering-btn-${puja.id}`}
                                >
                                  Book Offering
                                  <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </div>

                        {/* YouTube video if available */}
                        {puja.youtube_url && getYouTubeEmbedUrl(puja.youtube_url) && (
                          <div className="border-t border-purple-100/50 p-4 bg-purple-50/30">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="text-sm font-semibold text-gray-800">{t('temples.watchPrevious')}</span>
                            </div>
                            <div className="aspect-video rounded-xl overflow-hidden">
                              <iframe
                                src={getYouTubeEmbedUrl(puja.youtube_url)}
                                title={`${puja.name} video`}
                                className="w-full h-full"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="sticky top-24 space-y-6"
              >
                {/* Quick Book Card */}
                <div className="glass rounded-2xl p-6">
                  <h3 className="font-heading text-lg font-bold text-[#1A202C] mb-4">{t('temples.quickBook')}</h3>
                  <p className="text-[#718096] text-sm mb-4">
                    {t('temples.quickBookDesc')}
                  </p>
                  <div className="space-y-2">
                    {temple.pujas_available?.map((pujaName, i) => (
                      <div key={i} className="flex items-center space-x-2 text-sm text-[#4A5568]">
                        <Star className="w-4 h-4 text-[#FF6B00]" />
                        <span>{pujaName}</span>
                      </div>
                    ))}
                  </div>

                  {/* Offline Interest Button */}
                  <Button 
                    className="w-full mt-6 btn-saffron rounded-xl py-6 shadow-lg shadow-orange-200"
                    onClick={handleOfflineInterest}
                    data-testid="interested-offline-puja-side-btn"
                  >
                    <Heart className="mr-2 w-4 h-4 fill-current" />
                    Interested in offline puja
                  </Button>
                </div>

              </motion.div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TempleDetailPage;
